import { UserSearchResultResponse } from '@/components/chat/_domain/server/user-search-result-response';
import { IChatServerService, ServerServiceSubscriptionStore } from '@/app-modules/server-service';
import { UserSearchRequest } from '@/components/chat/_domain/modules/user-search-service/user-search-request';
import { IUserSearchResult } from '@/components/chat/_domain/modules/user-search-service/user-search-result';
import { IConfigurationService, IGamesResponseViewModel } from '@/app-modules/config';
import { IGameChatRoomViewModel } from '@/app-modules/config';
import { compareGameNames } from '@/components/game/_domain/helpers';

const USER_SEARCH_REQUEST_TIME_OUT_MILLISECONDS = 10000;

interface IChatRoom extends IGameChatRoomViewModel {
  GameName: string;
  ClubId: number;
  ClubName: string;
}

interface IUserSearchService {
  findUserByUsername(username: string): Promise<IUserSearchResult[]>;
  findCurrentClubNameForUserAndGame(username: string, gameName: string): Promise<string>;
  dispose(): void;
}

class UserSearchService implements IUserSearchService {
  private readonly subscriptionStore: ServerServiceSubscriptionStore;
  private readonly requestList: UserSearchRequest[] = [];
  private readonly chatRoomList: IChatRoom[] = [];

  constructor(private readonly server: IChatServerService, private readonly configurationService: IConfigurationService) {
    this.subscriptionStore = new ServerServiceSubscriptionStore(
      this.server.subscribeUserSearchResult((payload: UserSearchResultResponse) => this.handleUserSearchResult(payload))
    );
  }

  public async findUserByUsername(username: string): Promise<IUserSearchResult[]> {
    await this.initialiseChatRoomList();

    return new Promise<IUserSearchResult[]>((resolve, reject) => {
      let timerHandle: number = 0;
      const request = new UserSearchRequest(username, () => {
        window.clearTimeout(timerHandle);
        resolve(request.searchResults);
      });
      this.requestList.push(request);
      request.send(this.server);

      timerHandle = window.setTimeout(() => resolve([]), USER_SEARCH_REQUEST_TIME_OUT_MILLISECONDS);
    });
  }

  public async findCurrentClubNameForUserAndGame(username: string, gameName: string): Promise<string> {
    const searchResults = await this.findUserByUsername(username);
    const searchResult = searchResults.find((result) => result.gameName.forConfiguration() === gameName.forConfiguration());
    return searchResult ? searchResult.clubName : null;
  }

  public dispose() {
    this.subscriptionStore.unsubscribeAll();
  }

  private async initialiseChatRoomList(): Promise<void> {
    if (this.chatRoomList.length === 0) {
      const gameConfig = await this.configurationService.getGameConfiguration();
      this.chatRoomList.push(...this.reduceGameConfiguration(gameConfig));
    }
  }

  private reduceGameConfiguration(gameConfig: IGamesResponseViewModel): IChatRoom[] {
    const chatRooms: IChatRoom[] = [];
    chatRooms.push(...this.reduceBingoGameConfiguration(gameConfig));
    chatRooms.push(...this.reduceArcadeGameConfiguration(gameConfig));
    return chatRooms;
  }

  private reduceBingoGameConfiguration(gameConfig: IGamesResponseViewModel): IChatRoom[] {
    const games = gameConfig.Games || [];
    const chatRooms: IChatRoom[] = [];

    for (const game of games) {
      for (const club of game.Clubs) {
        for (const room of club.ChatRooms) {
          chatRooms.push(Object.assign({}, room, {
            GameName: game.Name,
            ClubId: club.Id,
            ClubName: club.Name
          }));
        }
      }
    }

    return chatRooms;
  }

  private reduceArcadeGameConfiguration(gameConfig: IGamesResponseViewModel): IChatRoom[] {
    const arcadeGameName = gameConfig.ArcadeChatrooms ? gameConfig.ArcadeChatrooms.Name : '';
    const arcadeChatRooms = gameConfig.ArcadeChatrooms ? (gameConfig.ArcadeChatrooms.ChatRooms || []) : [];
    const chatRooms: IChatRoom[] = [];

    for (const room of arcadeChatRooms) {
      chatRooms.push(Object.assign({}, room, {
        GameName: arcadeGameName,
        ClubId: 0,
        ClubName: arcadeGameName,
        IsArcade: true
      }));
    }

    return chatRooms;
  }

  private handleUserSearchResult(payload: UserSearchResultResponse): void {
    const userSearchRequest = this.findUserSearchRequestForToken(payload.requestToken);
    if (userSearchRequest) {
      const searchResults = this.buildSearchResultsFromUserSearchResultResponse(payload);
      userSearchRequest.resolve(searchResults);
      this.dequeueUserSearchRequest(userSearchRequest);
    }
  }

  private findUserSearchRequestForToken(requestToken: string): UserSearchRequest {
    return this.requestList.find((userSearchRequest) => userSearchRequest.requestToken === requestToken);
  }

  private buildSearchResultsFromUserSearchResultResponse(payload: UserSearchResultResponse): IUserSearchResult[] {
    const searchResults: IUserSearchResult[] = [];

    if (payload) {
      for (const response of payload.responses) {
        for (const result of response.results) {
          const chatRoom = this.findChatRoom(result.roomId, response.chatEngineName);
          searchResults.push({
            username: result.username,
            gameName: chatRoom ? chatRoom.GameName : response.chatEngineName,
            clubId: chatRoom ? chatRoom.ClubId : 0,
            clubName: chatRoom ? chatRoom.ClubName : null,
            roomId: result.roomId,
            roomName: chatRoom ? chatRoom.Name : 'Unknown'
          });
        }
      }
    }
    return searchResults;
  }

  private findChatRoom(roomId: number, gameName: string): IChatRoom {
    let rooms = this.chatRoomList.filter((room) => room.Id === roomId);

    // For Arcade, gameName is sometimes Arcade-[RoomName] (i.e. Arcade-Clover, Arcade-ShootingStar).
    // Reduce mapping issues - return if single result. Proceed only if Room ID returns more than one Game
    if (!rooms || rooms.length <= 1) {
      return rooms[0] || null;
    }

    rooms = rooms.filter((room) => room.GameName && compareGameNames(room.GameName, gameName) === 0);
    if (this.isEveryChatRoomForTheSameGame(rooms, gameName)) {
      let room = rooms[0];
      if (!this.isEveryChatRoomNamedTheSame(rooms)) {
        room = Object.assign({}, room, { Name: room.GameName });
      }
      return room;
    }

    return null;
  }

  private isEveryChatRoomForTheSameGame(rooms: IChatRoom[], gameName: string): boolean {
    return rooms && rooms.length !== 0 && rooms.every((room) => compareGameNames(room.GameName, gameName) === 0);
  }

  private isEveryChatRoomNamedTheSame(rooms: IChatRoom[]): boolean {
    if (rooms && rooms.length > 0) {
      const expectedRoomName = rooms[0].Name.toLowerCase();
      return rooms.every((room) => room.Name.toLowerCase() === expectedRoomName);
    }
    return false;
  }


  private dequeueUserSearchRequest(userSearchRequest: UserSearchRequest): void {
    const index = this.requestList.findIndex((request) => request.requestToken === userSearchRequest.requestToken);
    if (index >= 0) {
      this.requestList.splice(index, 1);
    }
  }
}

export {
  IUserSearchService,
  UserSearchService
};



// WEBPACK FOOTER //
// ./node_modules/tslint-loader??ref--0!./src/components/chat/_domain/modules/user-search-service/user-search-service.ts